import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import LoginForm from './components/LoginForm';
import TeacherRegistrationForm from './components/TeacherRegistrationForm';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import ResetPasswordForm from './components/ResetPasswordForm';
import TwoFactorChallengeForm from './components/TwoFactorChallengeForm';
import TwoFactorSetupForm from './components/TwoFactorSetupForm';
import AuthTabs from './components/AuthTabs';
import SuccessMessage from './components/SuccessMessage';
import { AuthMode, LoginFormData, TeacherRegistrationFormData, ForgotPasswordFormData, ResetPasswordFormData } from './types';
import { api } from '../../lib/api';
import { roleToRedirectPath, setToken } from '../../lib/auth';
import { useAuth } from '../../context/AuthContext';

const AuthenticationPortal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const androidApkUrl = String((import.meta as any).env?.VITE_ANDROID_APK_URL || '').trim();
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [actionUrl, setActionUrl] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState<string | null>(null);

  const [twoFaChallengeToken, setTwoFaChallengeToken] = useState<string | null>(null);
  const [twoFaSetupToken, setTwoFaSetupToken] = useState<string | null>(null);
  const [twoFaIssuer, setTwoFaIssuer] = useState<string>('Scola');
  const [twoFaManualKey, setTwoFaManualKey] = useState<string>('');
  const [twoFaOtpAuthUri, setTwoFaOtpAuthUri] = useState<string>('');
  const [twoFaRememberDevice, setTwoFaRememberDevice] = useState<boolean>(true);
  const [twoFaIdentifierLabel, setTwoFaIdentifierLabel] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const oauth = (params.get('oauth') || '').toLowerCase();
    if (oauth !== 'google') return;

    const token = params.get('token') || '';
    const error = params.get('error') || '';

    if (error) {
      setSuccessMessage(decodeURIComponent(error));
      setRedirectPath(null);
      setActionUrl(null);
      // Clean URL params.
      navigate('/authentication-portal', { replace: true });
      return;
    }

    if (!token) {
      setSuccessMessage('Autentificare Google eșuată.');
      setRedirectPath(null);
      setActionUrl(null);
      navigate('/authentication-portal', { replace: true });
      return;
    }

    let canceled = false;
    (async () => {
      setIsLoading(true);
      try {
        // OAuth flow stores token directly (persistent by default).
        setToken(token, true);
        const meResp = await api.get('/api/users/me');
        if (canceled) return;
        await refresh();

        const role: string | undefined = meResp.data?.role;
        const fullName: string | undefined = meResp.data?.full_name;
        const isPlatformAdmin: boolean = Boolean(meResp.data?.is_platform_admin);

        setSuccessMessage(`Bine ai venit${fullName ? `, ${fullName}` : ''}!`);
        if (!role) {
          setRedirectPath('/');
        } else if (role === 'admin') {
          setRedirectPath(isPlatformAdmin ? '/platform-admin-dashboard' : '/platform-admin-dashboard');
        } else {
          setRedirectPath(roleToRedirectPath(role));
        }
        setActionUrl(null);
      } catch {
        setSuccessMessage('Autentificare Google eșuată.');
        setRedirectPath(null);
        setActionUrl(null);
      } finally {
        if (!canceled) setIsLoading(false);
        // Clean URL params in all cases.
        navigate('/authentication-portal', { replace: true });
      }
    })();

    return () => {
      canceled = true;
    };
  }, [location.search, navigate, refresh]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const mode = (params.get('mode') || '').toLowerCase();
    const token = params.get('token') || '';

    if (mode === 'reset-password') {
      setAuthMode('reset-password');
      setResetToken(token || null);
      setSuccessMessage(null);
      setActionUrl(null);
      setRedirectPath(null);
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const oauth = (params.get('oauth') || '').toLowerCase();
    const mode = (params.get('mode') || '').toLowerCase();
    if (oauth === 'google') return;
    if (mode === 'reset-password') return;

    const confirmed = (params.get('email_confirmed') || '').trim();
    const error = (params.get('email_confirm_error') || '').trim();

    if (!confirmed && !error) return;

    setAuthMode('login');
    setActionUrl(null);
    setRedirectPath(null);

    if (confirmed === '1') {
      setSuccessMessage('Email confirmat! Te poți autentifica.');
    } else if (error === 'invalid_token') {
      setSuccessMessage('Link invalid sau expirat. Încearcă să te autentifici pentru retrimiterea confirmării.');
    } else if (error === 'user_missing') {
      setSuccessMessage('Cont inexistent. Poate ai folosit un link vechi.');
    } else {
      setSuccessMessage('Nu am putut confirma emailul. Încearcă din nou.');
    }

    navigate('/authentication-portal', { replace: true });
  }, [location.search, navigate]);


  const handleLogin = async (data: LoginFormData): Promise<void> => {
    setIsLoading(true);
    try {
      const identifier = (data.identifier || '').trim();
      const isEmail = identifier.includes('@');
      const resp = isEmail
        ? await api.post('/api/users/login', { email: identifier, password: data.password })
        : await api.post('/api/users/login-username', { username: identifier, password: data.password });

      if (resp?.data?.requires_2fa_setup) {
        const setupToken: string | undefined = resp.data?.setup_token;
        if (!setupToken) {
          alert('Autentificare eșuată: lipseste setup_token.');
          return;
        }
        setTwoFaRememberDevice(Boolean(data.rememberMe));
        setTwoFaSetupToken(setupToken);
        setTwoFaChallengeToken(null);
        setTwoFaIdentifierLabel(identifier);
        setAuthMode('setup-2fa');

        const setupResp = await api.post(
          '/api/users/2fa/setup/start',
          {},
          { headers: { Authorization: `Bearer ${setupToken}` } }
        );
        setTwoFaIssuer(String(setupResp.data?.issuer || 'Scola'));
        setTwoFaManualKey(String(setupResp.data?.manual_key || ''));
        setTwoFaOtpAuthUri(String(setupResp.data?.otpauth_uri || ''));
        return;
      }

      if (resp?.data?.requires_2fa) {
        const challengeToken: string | undefined = resp.data?.challenge_token;
        if (!challengeToken) {
          alert('Autentificare eșuată: lipseste challenge_token.');
          return;
        }
        setTwoFaRememberDevice(Boolean(data.rememberMe));
        setTwoFaChallengeToken(challengeToken);
        setTwoFaSetupToken(null);
        setTwoFaIdentifierLabel(identifier);
        setAuthMode('login-2fa');
        return;
      }

      const token: string | undefined = resp.data?.token;
      const role: string | undefined = resp.data?.role;
      const fullName: string | undefined = resp.data?.full_name;
      const isPlatformAdmin: boolean = Boolean(resp.data?.is_platform_admin);

      if (!token || !role) {
        alert('Autentificare esuata: raspuns invalid de la server.');
        return;
      }

      setToken(token, data.rememberMe);
      await refresh();
      setSuccessMessage(`Bine ai revenit${fullName ? `, ${fullName}` : ''}!`);
      if (role === 'admin') {
        setRedirectPath(isPlatformAdmin ? '/platform-admin-dashboard' : '/platform-admin-dashboard');
      } else {
        setRedirectPath(roleToRedirectPath(role));
      }
      setActionUrl(null);
    } catch (error: any) {
      const isNative =
        Boolean((window as any)?.Capacitor?.isNativePlatform?.()) ||
        String(window?.location?.protocol || '') === 'capacitor:' ||
        String(window?.location?.protocol || '') === 'file:';
      const apiBaseRaw = String(api.defaults.baseURL || '').trim();
      const apiBase = apiBaseRaw ? apiBaseRaw : 'same-origin (/api)';
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        const msg = detail.message || 'Autentificare eșuată.';
        const confirmUrl: string | undefined = detail.confirm_url;
        if (confirmUrl) {
          setSuccessMessage(msg);
          setRedirectPath(null);
          setActionUrl(confirmUrl);
          return;
        }
        alert(msg);
        return;
      }
      const isNetworkError =
        !error?.response ||
        error?.code === 'ERR_NETWORK' ||
        String(error?.message || '').toLowerCase().includes('network error');

      if (isNetworkError) {
        const baseMsg = `Nu pot contacta serverul API (${apiBase}).`;
        alert(
          isNative
            ? `${baseMsg}\n\nVerifica:\n- backend-ul ruleaza si asculta pe 0.0.0.0:8000 (nu doar pe 127.0.0.1)\n- telefonul si PC-ul sunt in aceeasi retea (Wi-Fi)\n- firewall-ul Windows permite conexiuni pe portul 8000\n- VITE_API_BASE_URL este setat corect (ex: http://192.168.x.x:8000)`
            : `${baseMsg}\n\nVerifica:\n- backend-ul ruleaza pe http://localhost:8000\n- daca rulezi frontend-ul cu Vite, exista proxy pentru /api catre backend (vite.config.ts)\n- sau seteaza VITE_API_BASE_URL_WEB cu URL-ul backend-ului (ex: http://localhost:8000)`
        );
        return;
      }

      alert(detail || 'A aparut o eroare la autentificare. Te rog incearca din nou.');
    } finally {
      setIsLoading(false);
    }
  };

  const finishLogin = async (payload: any, rememberMe: boolean): Promise<void> => {
    const token: string | undefined = payload?.token;
    const role: string | undefined = payload?.role;
    const fullName: string | undefined = payload?.full_name;
    const isPlatformAdmin: boolean = Boolean(payload?.is_platform_admin);

    if (!token || !role) {
      alert('Autentificare esuata: raspuns invalid de la server.');
      return;
    }

    setToken(token, rememberMe);
    await refresh();
    setSuccessMessage(`Bine ai revenit${fullName ? `, ${fullName}` : ''}!`);
    if (role === 'admin') {
      setRedirectPath(isPlatformAdmin ? '/platform-admin-dashboard' : '/platform-admin-dashboard');
    } else {
      setRedirectPath(roleToRedirectPath(role));
    }
    setActionUrl(null);
  };

  const handleTwoFaChallenge = async (otp: string): Promise<void> => {
    setIsLoading(true);
    try {
      const challenge = String(twoFaChallengeToken || '').trim();
      if (!challenge) {
        alert('Sesiune 2FA invalidă. Reîncearcă autentificarea.');
        setAuthMode('login');
        return;
      }
      const resp = await api.post('/api/users/login-2fa', {
        challenge_token: challenge,
        otp,
        remember_device: Boolean(twoFaRememberDevice),
      });
      await finishLogin(resp.data, Boolean(twoFaRememberDevice));
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      alert(detail || 'Cod 2FA invalid. Încearcă din nou.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFaSetupVerify = async (otp: string): Promise<void> => {
    setIsLoading(true);
    try {
      const setupToken = String(twoFaSetupToken || '').trim();
      if (!setupToken) {
        alert('Sesiune 2FA invalidă. Reîncearcă autentificarea.');
        setAuthMode('login');
        return;
      }
      const resp = await api.post(
        '/api/users/2fa/setup/verify',
        { otp, remember_device: Boolean(twoFaRememberDevice) },
        { headers: { Authorization: `Bearer ${setupToken}` } }
      );

      if (resp.data?.token) {
        await finishLogin(resp.data, Boolean(twoFaRememberDevice));
      } else {
        setSuccessMessage('2FA a fost activat. Te poți autentifica.');
        setRedirectPath('/authentication-portal');
        setActionUrl(null);
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      alert(detail || 'Cod 2FA invalid. Încearcă din nou.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTeacherRegistration = async (
  data: TeacherRegistrationFormData)
  : Promise<void> => {
    setIsLoading(true);
    try {
      const resp = await api.post('/api/users/register', {
        full_name: data.fullName.trim(),
        email: data.email,
        password: data.password,
        role: 'profesor',
        county: data.county,
        city: data.city,
        school_name: data.school === '__custom__' ? data.schoolCustom : data.school,
        education_level: data.educationLevel,
      });

      const confirmUrl: string | undefined = resp.data?.confirm_url;
      const emailSent: boolean | undefined = resp.data?.email_sent;
       setSuccessMessage(
         confirmUrl
           ? `Cont creat! Email-ul nu a putut fi trimis. Foloseste linkul de confirmare, apoi autentifica-te.`
           : emailSent === false
             ? `Cont creat! Email-ul nu a putut fi trimis (SMTP neconfigurat). Porneste Mailpit pe 127.0.0.1:1025 sau seteaza SMTP_HOST/SMTP_PORT, apoi cere un link nou incercand autentificarea.`
             : `Cont creat! Verifica email-ul pentru confirmare, apoi autentifica-te.`
       );
       setActionUrl(confirmUrl || null);
       setRedirectPath('/authentication-portal');
     } catch (error: any) {
       const detail = error?.response?.data?.detail;
       alert(detail || 'A aparut o eroare la inregistrare. Te rog incearca din nou.');
     } finally {
       setIsLoading(false);
     }
   };

  const handleForgotPassword = async (data: ForgotPasswordFormData): Promise<void> => {
    setIsLoading(true);
    try {
      await api.post('/api/users/forgot-password', { email: data.email });
      setSuccessMessage('Daca exista un cont cu acest email, vei primi un link pentru resetarea parolei.');
      setRedirectPath('/authentication-portal');
      setActionUrl(null);
    } catch (error: any) {
      alert('A aparut o eroare. Te rog incearca din nou.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (data: ResetPasswordFormData): Promise<void> => {
    setIsLoading(true);
    try {
      const token = resetToken || '';
      if (!token) {
        alert('Link invalid sau lipseste tokenul.');
        return;
      }
      await api.post('/api/users/reset-password', {
        token,
        new_password: data.newPassword,
        confirm_password: data.confirmPassword,
      });
      setSuccessMessage('Parola a fost resetata. Te poti autentifica.');
      setRedirectPath('/authentication-portal');
      setActionUrl(null);
      setResetToken(null);
      navigate('/authentication-portal', { replace: true });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      alert(detail || 'A aparut o eroare la resetarea parolei.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedirect = (path?: string | null) => {
    const target = (path ?? redirectPath) || null;
    if (!target) return;

    // Special case: after registration we want to return to the login tab
    // (same route), not stay on the success screen.
    if (target.startsWith('/authentication-portal')) {
      setAuthMode('login');
      setSuccessMessage(null);
      setActionUrl(null);
      setRedirectPath(null);
      navigate('/authentication-portal', { replace: true });
      return;
    }

    navigate(target);
  };

  const handleTabChange = (tab: AuthMode) => {
    if (!isLoading) {
      setAuthMode(tab);
      setSuccessMessage(null);
      setActionUrl(null);
    }
  };

  const handleBackToLogin = () => {
    setAuthMode('login');
    setSuccessMessage(null);
    setActionUrl(null);
    setResetToken(null);
    setTwoFaChallengeToken(null);
    setTwoFaSetupToken(null);
    navigate('/authentication-portal', { replace: true });
  };

  return (
    <>
      <Helmet>
        <title>Portal de autentificare - Scola</title>
        <meta
          name="description"
          content="Autentifică-te în Scola sau înregistrează-te ca profesor pentru a crea o școală. Acces securizat pentru profesori, elevi, părinți și administratori." />

      </Helmet>

      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl blur-3xl" />
              <div className="relative bg-card border border-border rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-[72px] h-[72px] rounded-lg">
                    <img src="/assets/scola-logo.png" alt="Scola" className="w-[64px] h-[64px] object-contain" />
                  </div>
                  <div>
                    <h1
                      className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-blue-700 via-sky-500 to-green-500 bg-clip-text text-transparent"
                      style={{ fontFamily: 'Sora, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}
                    >
                      Scola
                    </h1>
                    <p className="text-sm text-muted-foreground">Platformă de management educațional</p>
                  </div>
                </div>

                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1730ad428-1764655063619.png"
                  alt="Sală de clasă modernă cu elevi care folosesc laptopuri"
                  className="w-full h-64 object-cover rounded-lg mb-6" />


                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Users" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">
                        Platformă multi-rol
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Experiență unitară pentru profesori, elevi, părinți și administratori
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Icon name="BookOpen" size={20} className="text-accent" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">
                        Management academic complet
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Clase, teme, note și comunicare într-un singur loc
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                      <Icon name="Shield" size={20} className="text-success" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">
                        Sigură și fiabilă
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Securitate avansată pentru protejarea datelor educaționale
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full max-w-md mx-auto">
            <div className="bg-card border border-border rounded-2xl shadow-lg p-6 sm:p-8">
              <div className="lg:hidden flex items-center justify-center gap-2 mb-6">
                <div className="flex items-center justify-center w-[64px] h-[64px] rounded-md">
                  <img src="/assets/scola-logo.png" alt="Scola" className="w-[56px] h-[56px] object-contain" />
                </div>
                <span
                  className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-blue-700 via-sky-500 to-green-500 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Sora, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}
                >
                  Scola
                </span>
              </div>

              {successMessage ?
              <SuccessMessage
                message={successMessage}
                redirectPath={redirectPath || undefined}
                onRedirect={redirectPath ? () => handleRedirect(redirectPath) : undefined}
                autoRedirectSeconds={3}
                actionLabel={actionUrl ? 'Deschide linkul de confirmare' : undefined}
                onAction={actionUrl ? () => window.open(actionUrl, '_blank', 'noopener,noreferrer') : undefined} /> :


              <>
                  {(authMode === 'login' ||
                    authMode === 'register' ||
                    authMode === 'forgot-password' ||
                    authMode === 'reset-password') && (
                    <AuthTabs activeTab={authMode} onTabChange={handleTabChange} disabled={isLoading} />
                  )}


                  {authMode === 'login' &&
                <LoginForm
                  onSubmit={handleLogin}
                  onForgotPassword={() => setAuthMode('forgot-password')}
                  isLoading={isLoading} />

                }

                  {authMode === 'login-2fa' &&
                    <TwoFactorChallengeForm
                      identifierLabel={twoFaIdentifierLabel || undefined}
                      onSubmit={handleTwoFaChallenge}
                      onBack={handleBackToLogin}
                      isLoading={isLoading} />
                  }

                  {authMode === 'setup-2fa' &&
                    <TwoFactorSetupForm
                      issuer={twoFaIssuer}
                      manualKey={twoFaManualKey}
                      otpauthUri={twoFaOtpAuthUri}
                      onSubmit={handleTwoFaSetupVerify}
                      onBack={handleBackToLogin}
                      isLoading={isLoading} />
                  }

                  {authMode === 'register' &&
                <TeacherRegistrationForm
                  onSubmit={handleTeacherRegistration}
                  isLoading={isLoading} />

                }

                  {authMode === 'forgot-password' &&
                <ForgotPasswordForm
                  onSubmit={handleForgotPassword}
                  onBack={handleBackToLogin}
                  isLoading={isLoading} />

                }

                  {authMode === 'reset-password' &&
                <ResetPasswordForm
                  onSubmit={handleResetPassword}
                  onBack={handleBackToLogin}
                  isLoading={isLoading} />

                }
                </>
              }
            </div>

            {androidApkUrl ? (
              <div className="mt-4 bg-card border border-border rounded-2xl shadow-sm p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="Smartphone" size={18} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground">Aplicație Android (APK)</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Descarcă și instalează aplicația fără Play Store. Update-urile vin automat din site (ca la PWA).
                    </div>
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        iconName="Download"
                        onClick={() => window.open(androidApkUrl, '_blank', 'noopener,noreferrer')}
                      >
                        Descarcă APK
                      </Button>
                    </div>
                    <div className="mt-2 text-[11px] text-muted-foreground">
                      La prima instalare Android îți va cere permisiunea „Install unknown apps”.
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground">
                Continuând, ești de acord cu{' '}
                <Link
                  className="text-primary hover:underline"
                  to="/legal/termeni-si-conditii"
                  state={{ backgroundLocation: location }}
                >
                  Termenii și condițiile
                </Link>{' '}
                și{' '}
                <Link
                  className="text-primary hover:underline"
                  to="/legal/politica-de-confidentialitate"
                  state={{ backgroundLocation: location }}
                >
                  Politica de confidențialitate
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default AuthenticationPortal;
